import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "@/contexts/LanguageContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { ArrowRight, Calendar, Barbell, ShoppingBag, MapPin, Timer, Mountains } from "@phosphor-icons/react";
import { HighlightBadge } from "@/components/HighlightBadge";
import { shortDate } from "@/utils/dateUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/5f2268e3-e28d-4daf-953b-57b699ecd67e/images/cc3674d40219d09e17a4ded9d7e2a888dafb6df838acb4e3a7806770d14a2b40.png";
const EVENTS_IMG = "https://images.unsplash.com/photo-1611674156713-40c5e8b07d97?w=800";
const TRAINING_IMG = "https://static.prod-images.emergentagent.com/jobs/5f2268e3-e28d-4daf-953b-57b699ecd67e/images/098d473530f759d9b3c2d1e0351e6e4a832458e51185903a128dcdabd146b9de.png";
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

export default function Landing() {
  const [events, setEvents] = useState([]);
  const [products, setProducts] = useState([]);
  const [landing, setLanding] = useState({});
  const [landingLoaded, setLandingLoaded] = useState(false);
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();

  useEffect(() => {
    axios.get(`${API}/events`).then(r => setEvents(r.data.slice(0, 3))).catch(() => {});
    axios.get(`${API}/products`).then(r => setProducts(r.data.filter(p => p.featured).slice(0, 3))).catch(() => {});
    axios.get(`${API}/landing`)
      .then(r => setLanding(r.data || {}))
      .catch(() => {})
      .finally(() => setLandingLoaded(true));
  }, []);

  // Use admin-overridden values when present, fallback to translations
  const heroImg = landing.hero_image_url || HERO_IMG;
  const featEventsImg = landing.feat_events_image_url || EVENTS_IMG;
  const featTrainingImg = landing.feat_training_image_url || TRAINING_IMG;
  const featStoreImg = landing.feat_store_image_url || "";
  const heroLocation = landing.hero_location || t("hero_location");
  const heroTitle1 = landing.hero_title1 || t("hero_title1");
  const heroTitle2 = landing.hero_title2 || t("hero_title2");
  const heroDesc = landing.hero_desc || t("hero_desc");
  const ctaStoreLabel = landing.hero_cta_store_label || t("hero_cta_store");
  const ctaEventsLabel = landing.hero_cta_events_label || t("hero_cta_events");
  const ctaTrainingLabel = landing.hero_cta_training_label || (t("hero_cta_training") !== "hero_cta_training" ? t("hero_cta_training") : "Entrenamientos");
  const showStore = landing.show_cta_store !== false;
  const showEvents = landing.show_cta_events !== false;
  const showTraining = landing.show_cta_training !== false;
  const ctaTitle = landing.cta_title || t("cta_title");
  const ctaDesc = landing.cta_desc || t("cta_desc");
  const ctaContactLabel = landing.cta_contact_label || t("cta_contact");
  const ctaWhatsappUrl = landing.cta_whatsapp_url || "https://wa.me/56912345678";

  return (
    <main>
      {/* HERO */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={heroImg} alt="Trail running Patagonia" className="w-full h-full object-cover" />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-16">
          <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.15 } } }}>
            <motion.p variants={fadeUp} className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400">{heroLocation}</motion.p>
            <motion.h1
              variants={fadeUp}
              className="text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-none font-black uppercase mt-4 font-['Cabinet_Grotesk'] min-h-[3.5em] sm:min-h-[4em] lg:min-h-[4.5em]"
              style={{ visibility: landingLoaded ? "visible" : "hidden" }}
            >
              {heroTitle1}<br /><span className="text-[#FF4D00]">{heroTitle2}</span>
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="mt-6 text-base sm:text-lg text-gray-300 max-w-xl leading-relaxed whitespace-pre-wrap min-h-[3em]"
              style={{ visibility: landingLoaded ? "visible" : "hidden" }}
            >
              {heroDesc}
            </motion.p>
            <motion.div variants={fadeUp} className="mt-8 flex flex-wrap gap-4">
              {showEvents && <Link to="/eventos"><Button className="bg-[#FF4D00] hover:bg-[#E64500] text-white font-bold px-8 h-12 text-sm uppercase tracking-wider" data-testid="hero-cta-events">{ctaEventsLabel} <ArrowRight size={16} className="ml-2" /></Button></Link>}
              {showTraining && <Link to="/entrenamientos"><Button variant="outline" className="border-white/20 text-white hover:bg-white/10 h-12 px-8 text-sm uppercase tracking-wider" data-testid="hero-cta-training">{ctaTrainingLabel}</Button></Link>}
              {showStore && <Link to="/tienda"><Button variant="outline" className="border-white/20 text-white hover:bg-white/10 h-12 px-8 text-sm uppercase tracking-wider" data-testid="hero-cta-store">{ctaStoreLabel}</Button></Link>}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section className="py-24 sm:py-32" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}
            variants={{ visible: { transition: { staggerChildren: 0.15 } } }} className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Events Card */}
            <motion.div variants={fadeUp} className="md:col-span-7 bg-[#121212] border border-white/5 p-0 overflow-hidden card-hover group relative">
              <div className="relative h-64 overflow-hidden"><img src={featEventsImg} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" /><div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent" /></div>
              <div className="p-8">
                <div className="flex items-center gap-2 mb-3"><Calendar size={20} className="text-[#FF4D00]" /><span className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400">{t("feat_events_label")}</span></div>
                <h3 className="text-xl sm:text-2xl font-semibold mb-3 font-['Cabinet_Grotesk']">{t("feat_events_title")}</h3>
                <p className="text-sm text-gray-400 leading-relaxed mb-6 whitespace-pre-wrap">{t("feat_events_desc")}</p>
                <Link to="/eventos" className="text-[#FF4D00] text-sm font-bold hover:underline inline-flex items-center gap-1" data-testid="feature-events-link">{t("feat_events_link")} <ArrowRight size={14} /></Link>
              </div>
            </motion.div>
            {/* Training Card */}
            <motion.div variants={fadeUp} className="md:col-span-5 bg-[#121212] border border-white/5 p-0 overflow-hidden card-hover group relative">
              <div className="relative h-48 overflow-hidden"><img src={featTrainingImg} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" /><div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent" /></div>
              <div className="p-8">
                <div className="flex items-center gap-2 mb-3"><Barbell size={20} className="text-[#FF4D00]" /><span className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400">{t("feat_training_label")}</span></div>
                <h3 className="text-xl sm:text-2xl font-semibold mb-3 font-['Cabinet_Grotesk']">{t("feat_training_title")}</h3>
                <p className="text-sm text-gray-400 leading-relaxed mb-6 whitespace-pre-wrap">{t("feat_training_desc")}</p>
                <Link to="/entrenamientos" className="text-[#FF4D00] text-sm font-bold hover:underline inline-flex items-center gap-1" data-testid="feature-training-link">{t("feat_training_link")} <ArrowRight size={14} /></Link>
              </div>
            </motion.div>
            {/* Store Card */}
            <motion.div variants={fadeUp} className="md:col-span-12 bg-[#121212] border border-white/5 p-8 card-hover group relative flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3"><ShoppingBag size={20} className="text-[#FF4D00]" /><span className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400">{t("feat_store_label")}</span></div>
                <h3 className="text-xl sm:text-2xl font-semibold mb-3 font-['Cabinet_Grotesk']">{t("feat_store_title")}</h3>
                <p className="text-sm text-gray-400 leading-relaxed mb-6 whitespace-pre-wrap">{t("feat_store_desc")}</p>
                <Link to="/tienda" data-testid="feature-store-link"><Button className="bg-[#FF4D00] hover:bg-[#E64500] text-white font-bold">{t("feat_store_link")} <ArrowRight size={14} className="ml-2" /></Button></Link>
              </div>
              {featStoreImg ? (
                <div className="w-full md:w-72 h-48 md:h-56 bg-[#1A1A1A] border border-white/5 overflow-hidden flex-shrink-0"><img src={featStoreImg} alt="" className="w-full h-full object-cover" /></div>
              ) : (
                <div className="flex gap-4 overflow-hidden">{products.map(p => (<div key={p.id} className="w-36 h-36 bg-[#1A1A1A] border border-white/5 overflow-hidden flex-shrink-0"><img src={p.image_url} alt={p.name} className="w-full h-full object-cover" /></div>))}</div>
              )}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* UPCOMING EVENTS */}
      {events.length > 0 && (
        <section className="py-24 sm:py-32 bg-[#0a0a0a]" data-testid="upcoming-events-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={{ visible: { transition: { staggerChildren: 0.1 } } }}>
              <motion.div variants={fadeUp} className="flex items-end justify-between mb-12">
                <div><p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400 mb-2">{t("upcoming_label")}</p><h2 className="text-3xl sm:text-4xl tracking-tight font-bold uppercase font-['Cabinet_Grotesk']">{t("upcoming_title")}</h2></div>
                <Link to="/eventos" className="text-[#FF4D00] text-sm font-bold hover:underline hidden sm:inline-flex items-center gap-1">{t("view_all")} <ArrowRight size={14} /></Link>
              </motion.div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {events.map(ev => {
                  const distPrices = (ev.distances || []).map(d => Number(d.price || 0));
                  const minPrice = distPrices.length > 0 ? Math.min(...distPrices) : Number(ev.price || 0);
                  const isFree = minPrice === 0;
                  return (
                  <motion.div key={ev.id} variants={fadeUp} className="bg-[#121212] border border-white/5 overflow-hidden card-hover">
                    <div className="h-48 overflow-hidden relative">
                      <img src={ev.image_url} alt={ev.title} className="w-full h-full object-cover" />
                      <HighlightBadge text={ev.badge_text} color={ev.badge_color} />
                      {isFree && (
                        <span
                          data-testid="free-event-badge"
                          className="absolute top-2 right-2 bg-[#16A34A] text-white text-[10px] sm:text-xs font-black uppercase tracking-wider px-2.5 py-1 shadow-lg shadow-black/30 z-10 pointer-events-none"
                        >
                          {t("free_label")}
                        </span>
                      )}
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Badge variant="outline" className="text-[#FF4D00] border-[#FF4D00]/30 text-xs">{ev.difficulty}</Badge>
                        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-white/5 border border-white/10 px-2 py-0.5">
                          <Calendar size={14} className="text-[#FF4D00]" />
                          {shortDate(ev.date, "es-CL")}
                          {ev.start_time && <span className="text-[#FF4D00]"> · {ev.start_time} hrs</span>}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold mb-2 font-['Cabinet_Grotesk']">{ev.title}</h3>
                      <div className="flex items-center gap-4 text-xs text-gray-400 mb-3">
                        {ev.distance_km && <span className="flex items-center gap-1"><Timer size={14} />{ev.distance_km}K</span>}
                        {ev.elevation_m && <span className="flex items-center gap-1"><Mountains size={14} />{ev.elevation_m}m D+</span>}
                        <span className="flex items-center gap-1"><MapPin size={14} />{ev.location}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        {isFree ? (
                          <span className="text-[#16A34A] font-bold">{t("free_label")}</span>
                        ) : (
                          <span className="text-[#FF4D00] font-bold">{formatPrice(minPrice)}</span>
                        )}
                        <Link to="/eventos"><Button variant="ghost" size="sm" className="text-gray-400 hover:text-[#FF4D00]">{t("details")} <ArrowRight size={12} className="ml-1" /></Button></Link>
                      </div>
                    </div>
                  </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-24 sm:py-32 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF4D00]/10 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={{ visible: { transition: { staggerChildren: 0.15 } } }}>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl tracking-tight font-bold uppercase font-['Cabinet_Grotesk'] mb-4">{ctaTitle}</motion.h2>
            <motion.p variants={fadeUp} className="text-gray-400 max-w-md mx-auto mb-8 whitespace-pre-wrap">{ctaDesc}</motion.p>
            <motion.div variants={fadeUp} className="flex justify-center gap-4 flex-wrap">
              <Link to="/contacto"><Button className="bg-[#FF4D00] hover:bg-[#E64500] text-white font-bold px-8 h-12" data-testid="cta-contact-btn">{ctaContactLabel}</Button></Link>
              <a href={ctaWhatsappUrl} target="_blank" rel="noopener noreferrer"><Button variant="outline" className="border-white/20 text-white hover:bg-white/10 h-12 px-8" data-testid="cta-whatsapp-btn">WhatsApp</Button></a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
